import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useActions, useTranslation, useTypedSelector } from '../../../../hooks'
import { OrganizationInterface } from '../../../../ts/interfaces'
import AddedNewClientFrame from '../../../reusable/frames/new-client/AddedNewClientFrame'
import CompanyFormFrame from '../../../reusable/frames/new-client/CompanyFormFrame'
import NumberOfEmployeesFrame from '../../../reusable/frames/new-client/NumberOfEmployeesFrame'
import {
    ButtonSmallOpacityRed,
    ButtonSmallOpacityRedText,
    ExitIcon,
} from '../../../styled/buttons'
import { HeadingPrimary } from '../../../styled/typography'
import CancelModal from './modals/CancelModal'
import { FiArrowLeft } from 'react-icons/fi'
import { useDefaultCountryDenmark } from '../../../../hooks/useDefaultCountryDenmark'

const Container = styled.div`
    padding-top: 3rem;
    margin: 0 4rem;
    position: relative;
`

const HeadingContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const ArrowIcon = styled(FiArrowLeft)`
    font-size: 2.6rem;
    color: ${({ theme }) => `${theme.primary}50`};
    cursor: pointer;
    position: absolute;
    top: 12rem;
`

const LAST_STEP = 3

const AddNewClient: React.FC = () => {
    const { registerOrganization } = useActions()

    const defaultCountry = useDefaultCountryDenmark()

    const [isCancelModalActive, setIsCancelModalActive] = useState(false)
    const translation = useTranslation()

    const [currentPageNumber, setCurrentPageNumber] = useState(1)

    const selfOrganization = useTypedSelector(
        (state) => state.caseworker.identity
    ).organization

    useEffect(() => {
        if (defaultCountry) {
            updateOrganizationCountry(defaultCountry)
        }
        // eslint-disable-next-line
    }, [defaultCountry])

    const [organization, setOrganization] = useState<OrganizationInterface>({
        cprNumber: '',
        organizationName: '',
        email: '',
        countryCode: '+45',
        phone: '',
        address: '',
        city: '',
        zipCode: '',
        employeeCount: null,
        countryId: null,
    })

    const updateOrganization = (key: string, value: null | string | number) => {
        setOrganization((prev) => ({ ...prev, [key]: value }))
    }

    const updateOrganizationCountry = (country: any) => {
        setOrganization((prev) => ({
            ...prev,
            countryCode: country.calling_code,
            countryId: country.id,
        }))
    }

    const handlePageChangeWithRegister = async (pageNumber: number) => {
        if (pageNumber === LAST_STEP && selfOrganization) {
            const res: any = await registerOrganization(
                selfOrganization.id,
                organization
            )
            if (res) {
                setCurrentPageNumber(LAST_STEP)
            }
        } else {
            setCurrentPageNumber(pageNumber)
        }
    }

    const companyFormPageChange = (page: number) => {
        // CompanyFormFrame hardcodes setCurrentPageNumber(3); remap to our step 2
        if (page === 3) setCurrentPageNumber(2)
        else setCurrentPageNumber(page)
    }

    const employeeCountPageChange = async (page: number) => {
        // NumberOfEmployeesFrame hardcodes setCurrentPageNumber(4); remap to our step 3 (LAST_STEP) with registration
        if (page === 4) await handlePageChangeWithRegister(LAST_STEP)
        else setCurrentPageNumber(page)
    }

    const renderCurrentPage = () => {
        if (currentPageNumber === 1) {
            return (
                <CompanyFormFrame
                    organization={organization}
                    updateOrganization={updateOrganization}
                    setCurrentPageNumber={companyFormPageChange}
                    updateOrganizationCountry={(country: any) =>
                        updateOrganizationCountry(country)
                    }
                />
            )
        } else if (currentPageNumber === 2) {
            return (
                <NumberOfEmployeesFrame
                    setCurrentPageNumber={employeeCountPageChange}
                    updateOrganization={updateOrganization}
                />
            )
        } else if (currentPageNumber === LAST_STEP) {
            return <AddedNewClientFrame />
        }
        return null
    }

    return (
        <>
            {isCancelModalActive && (
                <CancelModal closeModal={() => setIsCancelModalActive(false)} />
            )}
            <Container>
                <HeadingContainer>
                    <HeadingPrimary>
                        {translation.addNewClientPartner.headlineAddNewClient}
                    </HeadingPrimary>
                    {currentPageNumber !== LAST_STEP && (
                        <ButtonSmallOpacityRed
                            onClick={() => setIsCancelModalActive(true)}
                        >
                            <ExitIcon />
                            <ButtonSmallOpacityRedText>
                                {translation.addNewClientPartner.buttonCancel}
                            </ButtonSmallOpacityRedText>
                        </ButtonSmallOpacityRed>
                    )}
                </HeadingContainer>

                {currentPageNumber !== 1 && currentPageNumber !== LAST_STEP && (
                    <ArrowIcon
                        onClick={() =>
                            setCurrentPageNumber(currentPageNumber - 1)
                        }
                    />
                )}

                {renderCurrentPage()}
            </Container>
        </>
    )
}

export default AddNewClient
